import { IGcpAppDispatch } from "../../redux/store";
import { performSignedRequest } from "../../redux/modules/auth/asyncActions";
import config from "../../config";
import { ISRS } from "../csu/site";
import { IFlatCoords } from "./sharedTypes";

// E.g. 2021-04-07T09:06:49.605Z
type TimestampIso = string;

export interface IFakeGcp {
  id: string;
  name: string;
  operatorCoords: IFlatCoords;
  tsCreated: TimestampIso;
  archivedBy: string | null;
  tsArchived: TimestampIso | null;
  addedBy: string;
  comment: string | null;
  commentedBy: string | null;
  tsCommented: TimestampIso | null;
}

export const fetchFakeGcps =
  (customerId: string, siteId: string) =>
  async (dispatch: IGcpAppDispatch): Promise<IFakeGcp[]> => {
    const url = `${config.endpoints.backofficeApi}/${customerId}/${siteId}/fake-gcps`;
    const resp = await dispatch(
      performSignedRequest(
        {
          method: "GET",
          url,
        },
        customerId,
        siteId,
      ),
    );
    if (!resp.data || !resp.data.fakeGcps) {
      throw new Error(`Invalid response ${resp.data}`);
    }
    return resp.data.fakeGcps;
  };

// SRS and operatorCoords should be removed in the next iteration
export interface ICreateFakeGcpRequestItem {
  // coordinates in global SRS with EPSG code
  operatorCoords: IFlatCoords;
  srs: ISRS;
  name: string;
  addedBy: string;
}

export const batchCreateFakeGcps =
  (
    customerId: string,
    siteId: string,
    fakeGcpSemiproducts: ICreateFakeGcpRequestItem[],
  ) =>
  async (dispatch: IGcpAppDispatch): Promise<IFakeGcp[]> => {
    const url = `${config.endpoints.backofficeApi}/${customerId}/${siteId}/fake-gcps`;
    let resp;
    try {
      resp = await dispatch(
        performSignedRequest(
          {
            method: "POST",
            url,
            data: fakeGcpSemiproducts,
          },
          customerId,
          siteId,
        ),
      );
    } catch (e) {
      if (
        e.response &&
        e.response.data &&
        e.response.data.body &&
        e.response.data.body.error
      ) {
        throw new Error(e.response.data.body.error);
      }
      throw e;
    }

    if (!resp.data) {
      throw new Error(`Invalid response ${resp.data}`);
    }
    return resp.data;
  };

interface IFakeGcpUpdateOptions {
  id: string;
  name: string;
  tsCreated: TimestampIso;
  archivedBy: string | null;
  tsArchived: TimestampIso | null;
  addedBy: string;
  operatorCoords: IFlatCoords;
  srs: ISRS;
  comment: string | null;
  commentedBy: string | null;
  tsCommented: TimestampIso | null;
}

export const updateFakeGcp =
  (customerId: string, siteId: string, fakeGcp: IFakeGcp, srs: ISRS) =>
  async (dispatch: IGcpAppDispatch): Promise<IFakeGcp> => {
    const {
      id,
      name,
      addedBy,
      archivedBy,
      tsArchived,
      tsCreated,
      operatorCoords,
      comment,
      commentedBy,
      tsCommented,
    } = fakeGcp;
    const url = `${config.endpoints.backofficeApi}/${customerId}/${siteId}/fake-gcps/${id}`;
    const updateOptions: IFakeGcpUpdateOptions = {
      id,
      name,
      addedBy,
      tsCreated,
      archivedBy,
      tsArchived,
      operatorCoords,
      comment,
      commentedBy,
      tsCommented,
      srs,
    };
    let resp;
    try {
      resp = await dispatch(
        performSignedRequest(
          {
            method: "PUT",
            url,
            data: {
              fakeGcp: updateOptions,
            },
          },
          customerId,
          siteId,
        ),
      );
    } catch (e) {
      if (
        e.response &&
        e.response.data &&
        e.response.data.body &&
        e.response.data.body.error
      ) {
        throw new Error(e.response.data.body.error);
      }
      throw e;
    }

    if (!resp.data) {
      throw new Error(`Invalid response ${resp.data}`);
    }
    return resp.data;
  };
